import { SpaceAvatar } from "./Spaces";
export { SpaceView as WaitingPoolView } from "./SpaceView";
import { FriendshipCards } from "./FriendshipCards";
import { BlockedPeople } from "./AccountBlocks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiErrorCopy } from "../shared/api-error-copy";
import {
  AGE_BANDS,
  CANDIDATE_VISIBLE_TEXT_LIMITS,
  CURRENT_RELATIONSHIP_GOALS,
  GENDER_MATCH_CATEGORIES,
  PARTICIPATION_ACKNOWLEDGEMENTS,
  type AgeBand,
  type CurrentRelationshipGoal,
  type GenderMatchCategory,
  type ParticipationAcknowledgementType,
  type PreviewLocaleId,
  type ProfileSource,
} from "../../waiting-pool-entry.v1";
import { ApiError, api } from "./api";
import { RevisionConflictNotice } from "./RevisionConflictNotice";
import { PairingExperience } from "./PairingExperience";
import { ConversationAbout, ConversationList, ConversationThread, useConversations } from "./Conversations";
import { enablePush, pushAvailability, type PushAvailability } from "./push";
import type { ConversationSummary } from "./pairing-types";
import { ParticipantFrame, EmptyMark, MenuRow, MenuGroup, AppIcon, LoadingState, type MainTab } from "./ParticipantShell";
import "./romantic-pool.css";

export type HomeScreen = "friendship-nickname" | "friendship" | "blocked" | "explore" | "connections" | "me" | "profile" | "preferences" | "participation" | "preview" | "acknowledgements" | "contacts" | "before-meet" | "result-scope";
import type { LocationSettingsPayload, ValidationIssue } from "./types";
import {
  DeliberateActionAttempt,
  graphemeCount,
  isRevisionConflictCode,
  participationStateHeading,
  waitingPoolErrorMessage,
  type RevisionConflictSection,
} from "../shared/waiting-pool-client";
import type {
  CandidateProfileDraftPayload,
  CandidateProfileFields,
  MatchingPreferencesPayload,
  ParticipationPayload,
  PreviewPayload,
  ProfileReadPayload,
} from "./waiting-pool-types";

const GOAL_LABELS: Record<CurrentRelationshipGoal, string> = {
  open_to_connection: "愿意认识新的人",
  dating: "约会",
  stable_partner: "稳定伴侣",
  long_term: "长期关系",
};
const GENDER_LABELS: Record<GenderMatchCategory, string> = {
  woman: "女性",
  man: "男性",
  nonbinary_or_gender_diverse: "非二元或性别多元",
};
const AGE_LABELS: Record<AgeBand, string> = {
  "18_24": "18–24",
  "25_29": "25–29",
  "30_34": "30–34",
  "35_39": "35–39",
  "40_49": "40–49",
  "50_59": "50–59",
  "60_plus": "60+",
};

const EMPTY_PROFILE: CandidateProfileFields = {
  displayName: "",
  ageBand: null,
  customGenderDisplayLabel: null,
  pronouns: null,
  shortIntroduction: "",
  beforeYouMeetMeText: null,
};

function profileFields(value: CandidateProfileFields): CandidateProfileFields {
  return {
    displayName: value.displayName,
    ageBand: value.ageBand,
    customGenderDisplayLabel: value.customGenderDisplayLabel,
    pronouns: value.pronouns,
    shortIntroduction: value.shortIntroduction,
    beforeYouMeetMeText: value.beforeYouMeetMeText,
  };
}

function errorDetails(error: unknown): ValidationIssue[] {
  if (!(error instanceof ApiError) || !error.details || typeof error.details !== "object") return [];
  const issues = (error.details as { issues?: unknown }).issues;
  return Array.isArray(issues) ? issues as ValidationIssue[] : [];
}

function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return waitingPoolErrorMessage(error.code, error.message);
  return error instanceof Error ? error.message : fallback;
}

function FieldIssues({ issues, path }: { issues: ValidationIssue[]; path: string }) {
  return <>{issues.filter((issue) => issue.path === path || issue.path.endsWith(`.${path}`))
    .map((issue) => <p className="field-error" role="alert" key={`${issue.path}-${issue.code}`}>
      {apiErrorCopy(issue.message)}
    </p>)}</>;
}

/** Section navigation is a whole row; its status stays quieter than its destination. */
function SectionRow({ children, detail, status, disabled, onClick }: {
  children: React.ReactNode;
  detail?: React.ReactNode;
  status?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return <button type="button" className="r4-menu-row romantic-section-row" disabled={disabled} onClick={onClick}>
    <span className="r4-row-copy"><strong>{children}</strong>{detail && <small>{detail}</small>}</span>
    {status && <span className="romantic-row-status">{status}</span>}
    <AppIcon name="chevron" />
  </button>;
}

function SectionRows({ children, label }: { children: React.ReactNode; label?: string }) {
  return <div className="r4-group romantic-section-panel" role="group" aria-label={label}>{children}</div>;
}

function ActionDialog({ dialogRef, title, children, busy, onClose }: {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  title: string;
  children: React.ReactNode;
  busy: boolean;
  onClose: () => void;
}) {
  return <dialog className="account-dialog participation-dialog" ref={dialogRef}
    onCancel={(event) => { if (busy) event.preventDefault(); else onClose(); }}>
    <h2>{title}</h2>
    {children}
  </dialog>;
}

/** The reads behind Explore and Me. App starts them as soon as the session is known (see prefetch in api.ts). */
export function waitingPoolReads(locale: PreviewLocaleId = "zh"): string[] {
  return [
    `/api/v1/participation?locale=${locale}`,
    `/api/v1/candidate-profile?locale=${locale}`,
    "/api/v1/candidate-profile/draft",
    `/api/v1/matching-preferences?locale=${locale}`,
    `/api/v1/location-settings?locale=${locale}`,
  ];
}

export function RomanticWaitingPoolView({ onJoinFriendship, onBack, onLocation, onAccount, onLogout, initialScreen = "explore", onScreenChange, onQuestionnaire, onInstall, hasSubmission = true, hasQuestionnaireDraft = false }: {
  onJoinFriendship?: () => void;
  initialScreen?: HomeScreen; onScreenChange?: (screen: HomeScreen) => void;
  onQuestionnaire?: () => void; onInstall?: () => void;
  hasSubmission?: boolean; hasQuestionnaireDraft?: boolean;
  onBack: () => void;
  /** Opens the single Location page; "preferred" scrolls to the Preferred Locations section. */
  onLocation: (focus?: "preferred") => void;
  onAccount: () => void;
  onLogout: () => void;
}) {
  const [pairingDetail, setPairingDetail] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [beforeMeetDirty, setBeforeMeetDirty] = useState(false);
  const [detailBackToken, setDetailBackToken] = useState(0);
  const [emptySurface, setEmptySurface] = useState("Now");
  const [screen, setScreen] = useState<HomeScreen>(initialScreen);
  const [leave, setLeave] = useState<{action:()=>void} | null>(null);
  const leaveDialog = useRef<HTMLDialogElement>(null);
  const logoutDialog = useRef<HTMLDialogElement>(null);
  const go = (next: HomeScreen) => {setPairingDetail(false);setScreen(next);onScreenChange?.(next);setNotice("");setActionError("");};
  const locale: PreviewLocaleId = "zh";
  const [loading, setLoading] = useState(true);
  const [participation, setParticipation] = useState<ParticipationPayload | null>(null);
  const [profileRead, setProfileRead] = useState<ProfileReadPayload | null>(null);
  const [draftRecord, setDraftRecord] = useState<CandidateProfileDraftPayload | null>(null);
  const [profile, setProfile] = useState<CandidateProfileFields>(EMPTY_PROFILE);
  const [preferences, setPreferences] = useState<MatchingPreferencesPayload | null>(null);
  const [savedPreferences, setSavedPreferences] = useState<MatchingPreferencesPayload | null>(null);
  const [location, setLocation] = useState<LocationSettingsPayload | null>(null);
  const [preview, setPreview] = useState<PreviewPayload | null>(null);
  const [previewSource, setPreviewSource] = useState<ProfileSource>("draft");
  const [notice, setNotice] = useState("");
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [revisionConflicts, setRevisionConflicts] = useState<
    Partial<Record<RevisionConflictSection, true>>
  >({});
  const [profileIssues, setProfileIssues] = useState<ValidationIssue[]>([]);
  const [busySection, setBusySection] = useState<string | null>(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [poolConfirmed, setPoolConfirmed] = useState(false);
  const [endPassword, setEndPassword] = useState("");
  const [endConfirmation, setEndConfirmation] = useState("");
  const [deleteProfileConfirmation, setDeleteProfileConfirmation] = useState("");
  const messagingEnabled = Boolean(participation && participation.participationStatus !== "not_entered");
  const conversations = useConversations(messagingEnabled);
  const [thread, setThread] = useState<ConversationSummary | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [push, setPush] = useState<PushAvailability | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushOfferDismissed, setPushOfferDismissed] = useState(() => { try { return localStorage.getItem("perhaps.push-offer") === "dismissed"; } catch { return false; } });
  const hasConversations = Boolean(conversations.items?.length);
  useEffect(() => {
    if (screen !== "connections" || !hasConversations || push !== null) return;
    void pushAvailability().then(setPush).catch(() => setPush("unsupported"));
  }, [screen, hasConversations, push]);
  // A tapped notification brings the app forward on Connections.
  useEffect(() => {
    const open = (event: MessageEvent) => { if (event.data?.type === "perhaps:open-connections") { setThread(null); setAboutOpen(false); go("connections"); } };
    navigator.serviceWorker?.addEventListener("message", open);
    return () => navigator.serviceWorker?.removeEventListener("message", open);
  });
  const pauseDialog = useRef<HTMLDialogElement>(null);
  const endDialog = useRef<HTMLDialogElement>(null);
  const deleteProfileDialog = useRef<HTMLDialogElement>(null);
  const profileConflictRef = useRef<HTMLDivElement>(null);
  const preferencesConflictRef = useRef<HTMLDivElement>(null);
  const attempts = useRef<Record<string, DeliberateActionAttempt>>({});
  const mutationInFlight = useRef(false);

  const attempt = (name: string) => {
    attempts.current[name] ??= new DeliberateActionAttempt();
    return attempts.current[name];
  };

  const load = useCallback(async (announce = false) => {
    setLoadError("");
    const [participationPath, profilePath, draftPath, preferencesPath, locationPath] = waitingPoolReads(locale);
    const [participationResult, profileResult, draftResult, preferenceResult, locationResult] =
      await Promise.all([
        api<ParticipationPayload>(participationPath),
        api<ProfileReadPayload>(profilePath),
        api<{ draft: CandidateProfileDraftPayload | null }>(draftPath),
        api<MatchingPreferencesPayload>(preferencesPath),
        api<LocationSettingsPayload>(locationPath),
      ]);
    setParticipation(participationResult);
    setProfileRead(profileResult);
    setDraftRecord(draftResult.draft);
    setProfile(profileFields(draftResult.draft ?? profileResult.published ?? EMPTY_PROFILE));
    setPreviewSource(draftResult.draft || !profileResult.published ? "draft" : "published");
    setPreferences(preferenceResult);
    setSavedPreferences(preferenceResult);
    setLocation(locationResult);
    if (participationResult.revisions.preview?.current === false) setPreview(null);
    if (announce) setNotice("已更新到最新状态");
  }, [locale]);

  const refreshParticipation = useCallback(async () => {
    setParticipation(await api<ParticipationPayload>(`/api/v1/participation?locale=${locale}`));
  }, [locale]);

  const refreshConfiguration = useCallback(async () => {
    const [participationResult, preferenceResult, locationResult] = await Promise.all([
      api<ParticipationPayload>(`/api/v1/participation?locale=${locale}`),
      api<MatchingPreferencesPayload>(`/api/v1/matching-preferences?locale=${locale}`),
      api<LocationSettingsPayload>(`/api/v1/location-settings?locale=${locale}`),
    ]);
    setParticipation(participationResult);
    setPreferences(preferenceResult);
    setSavedPreferences(preferenceResult);
    setLocation(locationResult);
  }, [locale]);


  useEffect(() => {
    void load().catch((caught) => setLoadError(messageFor(caught, "无法加载参与设置")))
      .finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (revisionConflicts.profile) profileConflictRef.current?.focus();
    else if (revisionConflicts.preferences) preferencesConflictRef.current?.focus();
  }, [revisionConflicts]);

  const conflictSection = (section: string, code: string): RevisionConflictSection | null => {
    if (code.startsWith("profile_") || section === "profile" || section === "publish") return "profile";
    if (code.startsWith("preferences_") || section === "preferences") return "preferences";
    return null;
  };

  const setConflict = (section: RevisionConflictSection) => {
    setRevisionConflicts((current) => ({ ...current, [section]: true }));
  };

  const clearConflict = (section: RevisionConflictSection) => {
    setRevisionConflicts((current) => {
      const next = { ...current };
      delete next[section];
      return next;
    });
  };

  const run = async (section: string, work: () => Promise<void>) => {
    if(mutationInFlight.current)return;
    mutationInFlight.current=true;
    setBusySection(section);
    setActionError("");
    setNotice("");
    try {
      await work();
      if (section === "profile" || section === "publish") clearConflict("profile");
      if (section === "preferences") clearConflict("preferences");
    } catch (caught) {
      const revisionConflict = caught instanceof ApiError && isRevisionConflictCode(caught.code);
      const target = caught instanceof ApiError ? conflictSection(section, caught.code) : null;
      if (revisionConflict && target) {
        await load();
        setConflict(target);
      } else {
        setActionError(messageFor(caught, "请求未能完成"));
        if (caught instanceof ApiError && [
          "participation_conflict", "idempotency_key_reused",
        ].includes(caught.code)) await load();
        else setProfileIssues(errorDetails(caught));
      }
    } finally {
      mutationInFlight.current=false;
      setBusySection(null);
    }
  };

  const profileDirty = useMemo(
    () => JSON.stringify(profile) !== JSON.stringify(draftRecord
      ? {
        displayName: draftRecord.displayName,
        ageBand: draftRecord.ageBand,
        customGenderDisplayLabel: draftRecord.customGenderDisplayLabel,
        pronouns: draftRecord.pronouns,
        shortIntroduction: draftRecord.shortIntroduction,
        beforeYouMeetMeText: draftRecord.beforeYouMeetMeText,
      }
      : profileRead?.published
        ? {
          displayName: profileRead.published.displayName,
          ageBand: profileRead.published.ageBand,
          customGenderDisplayLabel: profileRead.published.customGenderDisplayLabel,
          pronouns: profileRead.published.pronouns,
          shortIntroduction: profileRead.published.shortIntroduction,
          beforeYouMeetMeText: profileRead.published.beforeYouMeetMeText,
        }
        : EMPTY_PROFILE),
    [profile, draftRecord, profileRead],
  );
  const preferencesDirty = useMemo(() => {
    const comparable = (value: MatchingPreferencesPayload | null) => value ? {
      currentRelationshipGoal: value.currentRelationshipGoal,
      genderMatchCategory: value.genderMatchCategory,
      openToGenderCategories: value.openToGenderCategories,
      acceptedAgeBands: value.acceptedAgeBands,
      inboundLocation: value.inboundLocation,
    } : null;
    return JSON.stringify(comparable(preferences)) !== JSON.stringify(comparable(savedPreferences));
  }, [preferences, savedPreferences]);

  const saveProfile = () => run("profile", async () => {
    const result = await api<{ draft: CandidateProfileDraftPayload; previewStale: true }>(
      "/api/v1/candidate-profile/draft",
      {
        method: "PUT",
        body: JSON.stringify({
          expectedDraftRevision: draftRecord?.draftRevision ?? null,
          profile,
        }),
      },
    );
    setDraftRecord(result.draft);setPreviewSource("draft");
    setProfile(profileFields(result.draft));
    setProfileIssues(result.draft.issues);
    setPreview(null);
    setNotice("草稿已保存，发布前请再预览一次。");
    await refreshParticipation();
  });

  const preferenceBody = (source = preferences) => ({
    expectedRevision: source?.revision ?? null,
    currentRelationshipGoal: source?.currentRelationshipGoal ?? null,
    genderMatchCategory: source?.genderMatchCategory ?? null,
    openToGenderCategories: source?.openToGenderCategories ?? [],
    acceptedAgeBands: source?.acceptedAgeBands ?? [],
    inboundLocation: source?.inboundLocation ?? {
      mode: "same_city", countryRegionCodes: [], placeIds: [],
    },
  });

  const savePreferences = (pause = false) => run("preferences", async () => {
    const body = { ...preferenceBody(), ...(pause ? { activeOutcome: "save_and_pause" } : {}) };
    const actionAttempt = attempt("preferences-pause");
    const result = await api<Record<string, unknown>>("/api/v1/matching-preferences", {
      method: "PUT",
      headers: pause ? { "Idempotency-Key": actionAttempt.keyFor(body) } : undefined,
      body: JSON.stringify(body),
    });
    if (pause) actionAttempt.settle();
    setPreview(null);
    await refreshConfiguration();
    setNotice(pause ? "偏好已保存，已暂停匹配。" : "匹配偏好已保存。");
    void result;
  });

  const acknowledge = (type: ParticipationAcknowledgementType) => run(`ack-${type}`, async () => {
    const contract = PARTICIPATION_ACKNOWLEDGEMENTS[type];
    await api("/api/v1/participation/acknowledgements", {
      method: "POST",
      body: JSON.stringify({
        acknowledgementType: type,
        acknowledgementVersion: contract.versionId,
        accepted: true,
      }),
    });
    setNotice("确认已记录。");
    await refreshParticipation();
  });

  const createPreview = () => run("preview", async () => {
    if (!participation || !preferences?.revision) throw new Error("请先完成资料与匹配偏好");
    const profileRevision = previewSource === "draft"
      ? draftRecord?.draftRevision
      : profileRead?.published?.publishedRevision;
    if (!profileRevision) throw new Error("请先保存一份资料再预览");
    const result = await api<PreviewPayload>(
      `/api/v1/candidate-profile/preview?locale=${locale}`,
      {
        method: "POST",
        body: JSON.stringify({
          profileSource: previewSource,
          profileRevision,
          preferencesRevision: preferences.revision,
        }),
      },
    );
    setPreview(result);
    setNotice("预览已生成。");
    await refreshParticipation();
  });

  const enter = () => run("enter", async () => {
    if (!participation || !preview || preview.profileSource !== "draft" || !profile.ageBand) return;
    const body = {
      expectedParticipationRevision: participation.participationRevision,
      profileDraftRevision: preview.profileRevision,
      preferencesRevision: preview.preferencesRevision,
      ageBand: profile.ageBand,
      confirmAgeBand: ageConfirmed,
      confirmEnter: poolConfirmed,
    };
    const actionAttempt = attempt("enter");
    const transitionResult = await api<Partial<ParticipationPayload>>("/api/v1/participation/enter", {
      method: "POST",
      headers: { "Idempotency-Key": actionAttempt.keyFor(body) },
      body: JSON.stringify(body),
    });
    setParticipation(current=>current?{...current,...transitionResult}:current);
    actionAttempt.settle();
    setPreview(null);
    setAgeConfirmed(false);
    setPoolConfirmed(false);
    setNotice("已加入 Matching Pool。");
    await load();
  });

  const publish = () => run("publish", async () => {
    if (!preview || preview.profileSource !== "draft") return;
    const body = {
      draftRevision: preview.profileRevision,
      preferencesRevision: preview.preferencesRevision,
      confirmAgeBand: ageConfirmed,
    };
    const actionAttempt = attempt("publish");
    await api("/api/v1/candidate-profile/publish", {
      method: "POST",
      headers: { "Idempotency-Key": actionAttempt.keyFor(body) },
      body: JSON.stringify(body),
    });
    actionAttempt.settle();
    setPreview(null);
    setNotice("资料已更新，对方会看到这个版本。");
    await load();
  });

  const reconfirmAge = () => run("age", async () => {
    if (!profileRead?.published || !ageConfirmed) return;
    const body = {
      ageBand: profileRead.published.ageBand,
      expectedPublishedRevision: profileRead.published.publishedRevision,
      confirmed: true,
    };
    const actionAttempt = attempt("age");
    await api("/api/v1/candidate-profile/age-band-confirmation", {
      method: "POST",
      headers: { "Idempotency-Key": actionAttempt.keyFor(body) },
      body: JSON.stringify(body),
    });
    actionAttempt.settle();
    setNotice("年龄段已重新确认。");
    await load();
  });

  const pause = () => run("pause", async () => {
    if (!participation) return;
    const body = {
      expectedParticipationRevision: participation.participationRevision,
      confirmPause: true,
    };
    const actionAttempt = attempt("pause");
    const transitionResult = await api<Partial<ParticipationPayload>>("/api/v1/participation/pause", {
      method: "POST",
      headers: { "Idempotency-Key": actionAttempt.keyFor(body) },
      body: JSON.stringify(body),
    });
    setParticipation(current=>current?{...current,...transitionResult}:current);
    actionAttempt.settle();
    pauseDialog.current?.close();
    setPreview(null);
    setAgeConfirmed(false);
    setPoolConfirmed(false);
    setNotice("匹配已暂停。资料、偏好、账户和已解锁的联系方式都会保留。");
    await load();
  });

  const resume = () => run("resume", async () => {
    if (!participation || !preview || !profile.ageBand) return;
    const source = preview.profileSource;
    const body = {
      expectedParticipationRevision: participation.participationRevision,
      profileSource: source,
      ...(source === "published"
        ? { publishedRevision: preview.profileRevision }
        : { draftRevision: preview.profileRevision }),
      preferencesRevision: preview.preferencesRevision,
      ageBand: source === "published"
        ? profileRead?.published?.ageBand
        : profile.ageBand,
      confirmAgeBand: ageConfirmed,
      confirmResume: poolConfirmed,
    };
    const actionAttempt = attempt(`resume-${source}`);
    const transitionResult = await api<Partial<ParticipationPayload>>("/api/v1/participation/resume", {
      method: "POST",
      headers: { "Idempotency-Key": actionAttempt.keyFor(body) },
      body: JSON.stringify(body),
    });
    setParticipation(current=>current?{...current,...transitionResult}:current);
    actionAttempt.settle();
    setPreview(null);
    setAgeConfirmed(false);
    setPoolConfirmed(false);
    setNotice("已恢复匹配。");
    await load();
  });

  const endParticipation = () => run("end", async () => {
    if (!participation) return;
    const nonSecretIdentity = {
      expectedParticipationRevision: participation.participationRevision,
      confirmation: endConfirmation,
    };
    const key = attempt("end").keyFor(nonSecretIdentity);
    try {
      const transitionResult = await api<Partial<ParticipationPayload>>("/api/v1/participation/end", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ ...nonSecretIdentity, password: endPassword }),
      });
      setParticipation(current=>current?{...current,...transitionResult}:current);
      attempt("end").settle();
      endDialog.current?.close();
      setNotice("匹配已永久结束。账户和已锁定的问卷都会保留。");
      await load();
    } finally {
      setEndPassword("");
    }
  });

  const deleteRetainedProfile = () => run("delete-profile", async () => {
    const body = { confirmation: deleteProfileConfirmation };
    const actionAttempt = attempt("delete-profile");
    await api("/api/v1/candidate-profile", {
      method: "DELETE",
      headers: { "Idempotency-Key": actionAttempt.keyFor(body) },
      body: JSON.stringify(body),
    });
    actionAttempt.settle();
    deleteProfileDialog.current?.close();
    setDeleteProfileConfirmation("");
    setNotice("保留的展示资料已删除。账户、问卷和偏好都会保留。");
    await load();
  });

  useEffect(() => {setAgeConfirmed(false);setPoolConfirmed(false);}, [preview?.projectionFingerprint,preview?.profileRevision,preview?.preferencesRevision,preview?.locationRevision,profileRead?.published?.publishedRevision,draftRecord?.draftRevision,savedPreferences?.revision,location?.locationRevision]);
  const navigateOutside = (action:()=>void) => {
    if(mutationInFlight.current){setNotice("上一个操作还在进行，请稍候。");return;}
    if(profileDirty || preferencesDirty || beforeMeetDirty) {setLeave({action});leaveDialog.current?.showModal();}
    else action();
  };

  const patchPreference = (patch: Partial<MatchingPreferencesPayload>) => {
    setPreferences((current) => current ? { ...current, ...patch } : current);
    setPreview(null);setAgeConfirmed(false);setPoolConfirmed(false);
    attempt("preferences-pause").invalidate();
  };

  if (screen === "friendship") return <FriendshipCards onBack={() => go("me")} />;

  if (loading || !participation || !profileRead || !preferences || !location) {
    const fallbackTab: MainTab = screen === "explore" ? "explore" : screen === "connections" ? "connections" : "me";
    return <ParticipantFrame screenKey={`availability-${screen}`} tab={fallbackTab} onTab={next=>go(next)} balanced={loading || screen!=="me"}>
      <main>{loading ? <LoadingState /> : <h2>暂时无法读取匹配设置</h2>}
        {!loading && <><p role="alert">{loadError || "请稍后重试。"}</p><button className="button" onClick={()=>{setLoading(true);void load().catch(caught=>setLoadError(messageFor(caught,"无法读取参与设置"))).finally(()=>setLoading(false));}}>Try again</button></>}
        {!loading && screen === "me" && <div className="r4-group"><header className="r4-me-identity"><SpaceAvatar beforeSwitch={navigateOutside} name="Me" onJoin={onJoinFriendship ?? (()=>go("friendship"))}/><h1>Me</h1></header><MenuRow onClick={onQuestionnaire ?? onBack}>Questionnaire</MenuRow><MenuRow onClick={onAccount}>Account & Privacy</MenuRow><MenuRow onClick={onLogout}>Log out</MenuRow></div>}
      </main>
    </ParticipantFrame>;
  }

  const ended = participation.participationStatus === "ended";
  const paused = participation.participationStatus === "paused";
  const active = participation.participationStatus === "active";
  const canActOnPreview = Boolean(preview && participation.revisions.preview?.current && !profileDirty && !preferencesDirty);
  const acknowledgementLocale = locale === "zh" ? "zh" : "en";

  const tab: MainTab = screen === "explore" ? "explore" : screen === "connections" ? "connections" : "me";
  const rootScreen = ["explore","connections","me"].includes(screen);
  const title = ({blocked:"Blocked",profile:"Profile",preferences:"Private Preferences",participation:"Matching Pool",preview:"Profile Preview",acknowledgements:"Participation Terms",contacts:"My Contact Info","before-meet":"Before We Meet","result-scope":"Preferred Locations"} as Partial<Record<HomeScreen,string>>)[screen];
  const pairingScreen = ["explore","connections","contacts","before-meet","result-scope"].includes(screen);
  const summary = participation.participationStatus === "not_entered" ? "尚未加入 Matching Pool" : paused ? "已暂停匹配" : ended ? "已结束匹配" : participation.activeEligibilityState === "action_required" ? "需要确认参与设置" : "已加入 Matching Pool";
  const locationName = location.display?.displayName ?? location.display?.countryDisplayName ?? "地点尚未确认";
  const visibilitySummary = location.boundary.mode === "same_city" ? "允许同城看到我"
    : location.boundary.mode === "same_country_region" ? "允许同一国家或地区看到我"
    : location.boundary.mode === "selected_locations" ? "允许自选地点看到我" : "允许全球看到我";
  const turnOnPush = async () => {
    setPushBusy(true);
    try { setPush(await enablePush()); } catch { setPush("unsupported"); } finally { setPushBusy(false); }
  };
  const dismissPushOffer = () => { setPushOfferDismissed(true); try { localStorage.setItem("perhaps.push-offer", "dismissed"); } catch { /* per-device convenience only */ } };
  const pushOffer = pushOfferDismissed || (push !== "available" && push !== "needs_home_screen") ? undefined
    : <section className="r4-push-offer" aria-labelledby="push-offer-heading">
      <h3 id="push-offer-heading">新消息通知</h3>
      <p>{push === "available" ? "有新消息时，这台设备会收到一条通知。通知里不会出现消息内容和对方的名字。" : "添加到主屏幕后，这台 iPhone 才能收到新消息通知。通知里不会出现消息内容和对方的名字。"}</p>
      <div className="r4-choice-actions">
        {push === "available"
          ? <button className="button" disabled={pushBusy} aria-busy={pushBusy || undefined} onClick={() => void turnOnPush()}>Turn on</button>
          : <button className="button" onClick={() => navigateOutside(onInstall ?? onBack)}>Add to Home Screen</button>}
        <button className="button" disabled={pushBusy} onClick={dismissPushOffer}>Not now</button>
      </div>
    </section>;
  const participationDecision = !ended && <section hidden={screen!=="acknowledgements" && !(screen==="participation"&&active) && !(screen==="preview"&&active)} className="participation-section primary-decision" aria-labelledby="next-action-heading">
        <h2 id="next-action-heading" className="visually-hidden">{active ? "管理匹配" : paused ? "恢复匹配" : "加入 Matching Pool"}</h2>
        {(!active || (screen==="preview" && preview?.profileSource==="draft")) && <div className="confirmation-list compact-confirmations">
          <label className="confirmation-row"><input type="checkbox" checked={ageConfirmed}
            onChange={(event) => setAgeConfirmed(event.target.checked)} />
            <span>我确认当前年龄段准确。</span></label>
          {!active && <label className="confirmation-row"><input type="checkbox" checked={poolConfirmed}
            onChange={(event) => setPoolConfirmed(event.target.checked)} />
            <span>我确认加入或恢复匹配。</span></label>}
        </div>}
        {active && participation.actionRequiredReasons.includes("age_band_confirmation_stale") &&
          <label className="confirmation-row age-reconfirmation">
            <input type="checkbox" checked={ageConfirmed}
              onChange={(event) => setAgeConfirmed(event.target.checked)} />
            <span>我确认已发布资料里的年龄段仍然准确。</span>
          </label>}
        <div className="section-actions">
          {participation.participationStatus === "not_entered" && <button className="button primary"
            disabled={!hasSubmission || !canActOnPreview || preview?.profileSource !== "draft" || !ageConfirmed || !poolConfirmed || !participation.acknowledgementVersions.current.adultDeclaration || !participation.acknowledgementVersions.current.matchingPoolAcknowledgement || busySection === "enter"}
            onClick={() => void enter()}>加入 Matching Pool</button>}
          {paused && <button className="button primary"
            disabled={!canActOnPreview || !ageConfirmed || !poolConfirmed || !participation.acknowledgementVersions.current.adultDeclaration || !participation.acknowledgementVersions.current.matchingPoolAcknowledgement || busySection === "resume"}
            onClick={() => void resume()}>{preview?.profileSource === "draft" ? "发布草稿并恢复匹配" : "使用当前资料恢复匹配"}</button>}
          {active && preview?.profileSource === "draft" && <button className="button primary"
            disabled={!canActOnPreview || !ageConfirmed || busySection === "publish"}
            onClick={() => void publish()}>发布这份资料</button>}
          {active && participation.actionRequiredReasons.includes("age_band_confirmation_stale") &&
            <button className="button primary" disabled={!ageConfirmed || busySection === "age"}
              onClick={() => void reconfirmAge()}>重新确认当前年龄段</button>}
          {active && <button className="button secondary" disabled={profileDirty || preferencesDirty}
            onClick={() => pauseDialog.current?.showModal()}>暂停匹配</button>}
        </div>
        {active && (profileDirty || preferencesDirty) &&
          <p className="action-reason">先保存或撤销当前编辑内容，再暂停匹配。</p>}
        {!canActOnPreview && !active && <p className="action-reason">请先完成设置并预览资料。</p>}
      </section>;
  const mainFrame = <ParticipantFrame className={screen === "participation" ? "romantic-pool-frame" : ""} dock={screen === "participation" && active ? participationDecision : undefined} screenKey={`${screen}-${pairingDetail}`} title={pairingDetail?(screen==="connections"?"Connection":"Profile"):title} onBack={pairingDetail?()=>setDetailBackToken(value=>value+1):rootScreen?undefined:()=>go("me")} tab={pairingDetail?undefined:tab} onTab={next=>go(next)} onRefresh={participation.participationStatus!=="not_entered"&&!pairingDetail&&(screen==="explore"||screen==="connections")?()=>{if(refreshing)return;setRefreshing(true);if(screen==="connections")void conversations.refresh().finally(()=>setRefreshing(false));else setRefreshToken(value=>value+1);}:undefined} badges={{ connections: conversations.unread }} refreshing={refreshing} actions={pairingScreen?<>{screen==="explore"&&!pairingDetail&&<button className="r4-icon" aria-label="Preferred Locations" onClick={()=>navigateOutside(()=>onLocation("preferred"))}><AppIcon name="settings" /></button>}</>:undefined}>
    <main className="participation-main romantic-settings">
      {screen === "blocked" && <BlockedPeople onChanged={() => void conversations.refresh()} />}
      {screen === "me" && <section className="r4-me">
        <header className="r4-me-identity">
          <SpaceAvatar beforeSwitch={navigateOutside} name={profileRead.published?.displayName || profile.displayName || "Me"} onJoin={onJoinFriendship ?? (()=>go("friendship"))}/>
          <div><h1>{profileRead.published?.displayName || profile.displayName || "Me"}</h1><p className={`r4-state${active && participation.activeEligibilityState === "eligible" ? "" : " quiet"}`}>{summary}</p></div>
        </header>
        <MenuGroup label="About me"><MenuRow icon="me" detail="私人草稿" onClick={()=>go("profile")}>Profile</MenuRow><MenuRow icon="questionnaire" detail={hasSubmission?"已提交":hasQuestionnaireDraft?"尚未提交":"尚未开始"} onClick={()=>navigateOutside(onQuestionnaire ?? onBack)}>Questionnaire</MenuRow></MenuGroup>
        <MenuGroup label="Matching"><MenuRow icon="participation" onClick={()=>go("participation")}>Participation</MenuRow><MenuRow icon="settings" onClick={()=>go("preferences")}>Private Preferences</MenuRow><MenuRow icon="location" onClick={()=>navigateOutside(()=>onLocation())}>Location</MenuRow></MenuGroup>
        {participation.participationStatus !== "not_entered" && <MenuGroup label="Before meeting"><MenuRow icon="connections" onClick={()=>go("contacts")}>My Contact Info</MenuRow><MenuRow icon="note" detail={beforeMeetDirty?"有未保存的修改":undefined} onClick={()=>go("before-meet")}>Before We Meet</MenuRow></MenuGroup>}
        <MenuGroup label="Account"><MenuRow icon="lock" onClick={()=>go("blocked")}>Blocked</MenuRow><MenuRow icon="lock" onClick={()=>navigateOutside(onAccount)}>Account & Privacy</MenuRow><MenuRow icon="home" onClick={()=>navigateOutside(onInstall ?? onBack)}>Add to Home Screen</MenuRow></MenuGroup>
        <button className="text-button r4-logout" onClick={()=>logoutDialog.current?.showModal()}>Log out</button>
      </section>}
      {pairingScreen && screen!=="result-scope" && participation.participationStatus === "not_entered" && <section className="r4-empty-scene">
        {screen === "explore" && <nav className="pairing-nav" aria-label="Explore sections" style={{"--seg-count": 4, "--seg-index": Math.max(0, ["Now","Interest","Activity","History"].indexOf(emptySurface))} as React.CSSProperties}>{["Now","Interest","Activity","History"].map(label=><button key={label} aria-current={label===emptySurface?"page":undefined} onClick={()=>setEmptySurface(label)}>{label}</button>)}</nav>}
        <div className="r4-empty-content"><EmptyMark /><h2>{screen==="connections"?"No connections yet":emptySurface==="Interest"?"Nothing new yet":emptySurface==="Activity"?"No activity yet":emptySurface==="History"?"No history yet":"No one yet"}</h2>{screen==="connections"&&<p>双方都愿意进一步认识之后，可以在这里说话。</p>}{screen!=="connections"&&emptySurface==="Now"&&<><p>{hasSubmission?"问卷已提交，接下来可以准备展示资料。":"完成问卷后，可以选择加入。"}</p><button className="button primary" onClick={()=>hasSubmission?go("participation"):navigateOutside(onQuestionnaire ?? onBack)}>{hasSubmission?"准备加入":"填写问卷"}</button></>}</div>
      </section>}
      {(notice || loadError || actionError) && <div className="participation-feedback" aria-live="polite">
        {notice && <p className="success-message">{notice}</p>}
        {loadError && <p className="error-message" role="alert">{loadError}</p>}
        {actionError && <p className="error-message" role="alert">{actionError}</p>}
      </div>}

      <section hidden={screen!=="participation"} className={`participation-state ${participation.uiState ?? "active"}`} aria-labelledby="participation-state-heading">
        <h2 id="participation-state-heading">{participationStateHeading(participation.participationStatus, participation.uiState ?? "active")}</h2>
        {participation.uiState === "action_required" && <>
          <p>{apiErrorCopy(participation.eligibilityIssues[0]?.message ?? "请先补全以下设置。")}</p>
          {participation.eligibilityIssues.length > 1 && <ol className="ordered-issues">
            {participation.eligibilityIssues.slice(1).map((issue) =>
              <li key={`${issue.path}-${issue.code}`}>{apiErrorCopy(issue.message)}</li>)}
          </ol>}
        </>}
        {paused && <p>可以恢复匹配。恢复前，请重新预览已发布的资料或草稿。</p>}
        {ended && <p>无法恢复匹配，展示资料也已隐藏。账户、已锁定的问卷和已解锁的联系仍会保留。</p>}
        <SectionRows label="Preparation">
          {!hasSubmission && <SectionRow onClick={()=>navigateOutside(onQuestionnaire ?? onBack)} status="提交后才能加入">Questionnaire</SectionRow>}
          <SectionRow onClick={()=>go("profile")} status={draftRecord?draftRecord.recordState==="complete"?"草稿已保存":"草稿需要补充":profileRead.published?"已发布":"尚未填写"}>Profile</SectionRow>
          <SectionRow onClick={()=>go("preferences")} status={preferences.recordState==="complete"?"已保存":"需要补充"}>Private Preferences</SectionRow>
          <SectionRow onClick={()=>navigateOutside(()=>onLocation())} detail={`${locationName} · ${visibilitySummary}`}>Location &amp; Visibility</SectionRow>
          {!ended&&<SectionRow onClick={()=>go("preview")}>Profile Preview</SectionRow>}
          {!ended&&<SectionRow onClick={()=>go("acknowledgements")} status={participation.acknowledgementVersions.current.adultDeclaration&&participation.acknowledgementVersions.current.matchingPoolAcknowledgement?"已确认":"需要确认"}>Participation Terms</SectionRow>}
        </SectionRows>
        {location.location.resolutionStatus === "pending" && <p className="section-intro">所在地还在等待确认。</p>}
        {ended && <p className="action-reason">永久结束匹配后，无法再修改地点和匹配偏好。</p>}

      </section>

      {messagingEnabled && screen === "connections" && <ConversationList items={conversations.items} error={conversations.error}
        onRetry={() => void conversations.refresh()} onOpen={(summary) => { setThread(summary); setAboutOpen(false); }}
        offer={pushOffer} />}
      {participation.participationStatus !== "not_entered" && <div hidden={!pairingScreen || screen === "connections"}><PairingExperience
        onCapabilityChange={refreshParticipation} onDetailChange={pairingScreen?setPairingDetail:undefined} backToken={detailBackToken} refreshToken={refreshToken}
        onRefreshSettled={()=>setRefreshing(false)} onBeforeMeetDirtyChange={setBeforeMeetDirty}
        panel={screen === "connections" ? "connections" : screen === "contacts" ? "contacts" : screen === "before-meet" ? "before-meet" : screen === "result-scope" ? "result-scope" : "explore"}
        onConfiguration={()=>go("participation")}
        mode={ended ? "ended" : paused ? "paused" : participation.activeEligibilityState === "eligible" ? "active" : "action_required"}
      /></div>}


      <section hidden={screen!=="profile"} className="participation-section" id="candidate-profile" aria-labelledby="profile-heading">
        <h2 id="profile-heading">{ended ? "保留资料" : "编辑私人草稿"}</h2>
        {revisionConflicts.profile && <RevisionConflictNotice
          section="profile"
          ref={profileConflictRef}
          onDismiss={() => clearConflict("profile")}
        />}
        {ended && !profileRead.published ? (
          <p className="section-intro">展示资料已删除。账户、已锁定的问卷、匹配偏好和确认记录仍会保留。</p>
        ) : <>
          <p className="section-intro">{active
          ? "草稿预览并发布后，对方才会看到新的资料；在此之前，展示的仍是原来的版本。"
          : "草稿只有自己能看见。保存之后，还要经过预览和确认，才会发布或加入匹配。"}</p>
          <div className="privacy-inline">
            请勿填写联系方式、社交账号、网址、具体单位、学校或住址。系统只能识别部分个人信息，换种写法也可能泄露隐私。
          </div>
          <div className="participation-form">
            <label htmlFor="profile-display-name">显示名称 <span>必填</span></label>
            <input id="profile-display-name" disabled={ended || busySection !== null} value={profile.displayName}
              onChange={(event) => { setProfile({ ...profile, displayName: event.target.value }); setPreview(null); setAgeConfirmed(false);setPoolConfirmed(false); }} />
            <small>{graphemeCount(profile.displayName)} / {CANDIDATE_VISIBLE_TEXT_LIMITS.display_name}</small>
            <FieldIssues issues={profileIssues} path="displayName" />

          <label htmlFor="profile-age-band">年龄段 <span>必填</span></label>
          <select id="profile-age-band" disabled={ended || busySection !== null} value={profile.ageBand ?? ""}
            onChange={(event) => { setProfile({ ...profile, ageBand: event.target.value as AgeBand }); setPreview(null);setAgeConfirmed(false);setPoolConfirmed(false); }}>
            <option value="">请选择</option>
            {AGE_BANDS.map((value) => <option value={value} key={value}>{AGE_LABELS[value]}</option>)}
          </select>

          <label htmlFor="profile-custom-gender">希望展示的性别称呼 <span>可选</span></label>
          <input id="profile-custom-gender" disabled={ended || busySection !== null} value={profile.customGenderDisplayLabel ?? ""}
            onChange={(event) => { setProfile({ ...profile, customGenderDisplayLabel: event.target.value || null }); setPreview(null);setAgeConfirmed(false);setPoolConfirmed(false); }} />
          <small>{graphemeCount(profile.customGenderDisplayLabel ?? "")} / {CANDIDATE_VISIBLE_TEXT_LIMITS.custom_gender_display_label}</small>

          <label htmlFor="profile-pronouns">代词 <span>可选</span></label>
          <input id="profile-pronouns" disabled={ended || busySection !== null} value={profile.pronouns ?? ""}
            onChange={(event) => { setProfile({ ...profile, pronouns: event.target.value || null }); setPreview(null);setAgeConfirmed(false);setPoolConfirmed(false); }} />
          <small>{graphemeCount(profile.pronouns ?? "")} / {CANDIDATE_VISIBLE_TEXT_LIMITS.pronouns}</small>

          <label htmlFor="profile-introduction">简短介绍 <span>必填</span></label>
          <textarea id="profile-introduction" disabled={ended || busySection !== null} rows={6} value={profile.shortIntroduction}
            onChange={(event) => { setProfile({ ...profile, shortIntroduction: event.target.value }); setPreview(null);setAgeConfirmed(false);setPoolConfirmed(false); }} />
          <small>{graphemeCount(profile.shortIntroduction)} / {CANDIDATE_VISIBLE_TEXT_LIMITS.short_introduction}</small>
          <FieldIssues issues={profileIssues} path="shortIntroduction" />

          <label htmlFor="profile-before-meet">见面前希望对方知道 <span>可选</span></label>
          <textarea id="profile-before-meet" disabled={ended || busySection !== null} rows={8} value={profile.beforeYouMeetMeText ?? ""}
            onChange={(event) => { setProfile({ ...profile, beforeYouMeetMeText: event.target.value || null }); setPreview(null);setAgeConfirmed(false);setPoolConfirmed(false); }} />
          <small>{graphemeCount(profile.beforeYouMeetMeText ?? "")} / {CANDIDATE_VISIBLE_TEXT_LIMITS.before_you_meet_me_text}</small>
          </div>
          {!ended && <div className="section-actions">
            <button className="button secondary" disabled={!profileDirty || busySection === "profile"}
              onClick={() => void saveProfile()}>{busySection === "profile" ? "Saving" : "保存私人草稿"}</button>
          </div>}
          {!ended && !profileDirty && draftRecord && <SectionRows><SectionRow onClick={()=>go("preview")}>Profile Preview</SectionRow></SectionRows>}
        </>}
      </section>

      <section hidden={screen!=="preferences"} className="participation-section" id="matching-preferences" aria-labelledby="preferences-heading">
        <h2 id="preferences-heading">当前匹配偏好</h2>
        {revisionConflicts.preferences && <RevisionConflictNotice
          section="preferences"
          ref={preferencesConflictRef}
          onDismiss={() => clearConflict("preferences")}
        />}
        <p className="section-intro">愿意认识的性别和年龄段，只用来判断双方是否符合彼此的条件，不会展示给对方。用于匹配的性别分类，也无法完整描述一个人的身份。</p>
        <div className="participation-form">
          <label htmlFor="relationship-goal">当前关系目标</label>
          <select id="relationship-goal" disabled={ended || busySection !== null}
            value={preferences.currentRelationshipGoal ?? ""}
            onChange={(event) => patchPreference({ currentRelationshipGoal: event.target.value as CurrentRelationshipGoal })}>
            <option value="">{preferences.suggestedCurrentRelationshipGoal
              ? `可选择：${GOAL_LABELS[preferences.suggestedCurrentRelationshipGoal]}`
              : "请选择"}</option>
            {CURRENT_RELATIONSHIP_GOALS.map((value) => <option value={value} key={value}>{GOAL_LABELS[value]}</option>)}
          </select>
          <fieldset disabled={ended || busySection !== null}>
            <legend>用于匹配的性别分类</legend>
            <div className="option-list compact">
              {GENDER_MATCH_CATEGORIES.map((value) => <label className="option-row" key={value}>
                <input type="radio" name="gender-category"
                  checked={preferences.genderMatchCategory === value}
                  onChange={() => patchPreference({ genderMatchCategory: value })} />
                <span>{GENDER_LABELS[value]}</span>
              </label>)}
            </div>
          </fieldset>
          <fieldset disabled={ended || busySection !== null}>
            <legend>愿意认识的性别 <small>对方不可见</small></legend>
            <div className="option-list compact">
              {GENDER_MATCH_CATEGORIES.map((value) => <label className="option-row" key={value}>
                <input type="checkbox" checked={preferences.openToGenderCategories.includes(value)}
                  onChange={(event) => patchPreference({
                    openToGenderCategories: event.target.checked
                      ? [...preferences.openToGenderCategories, value]
                      : preferences.openToGenderCategories.filter((item) => item !== value),
                  })} />
                <span>{GENDER_LABELS[value]}</span>
              </label>)}
            </div>
          </fieldset>
          <fieldset disabled={ended || busySection !== null}>
            <legend>愿意认识的年龄段 <small>对方不可见</small></legend>
            <div className="option-list compact">
              {AGE_BANDS.map((value) => <label className="option-row" key={value}>
                <input type="checkbox" checked={preferences.acceptedAgeBands.includes(value)}
                  onChange={(event) => patchPreference({
                    acceptedAgeBands: event.target.checked
                      ? [...preferences.acceptedAgeBands, value]
                      : preferences.acceptedAgeBands.filter((item) => item !== value),
                  })} />
                <span>{AGE_LABELS[value]}</span>
              </label>)}
            </div>
          </fieldset>
        </div>
        {preferences.revision !== null && preferences.issues.length > 0 && <ul className="field-errors">
          {preferences.issues.map((issue) => <li key={`${issue.path}-${issue.code}`}>{apiErrorCopy(issue.message)}</li>)}
        </ul>}
        {!ended && <div className="section-actions">
          <button className="button secondary" disabled={busySection === "preferences"}
            onClick={() => void savePreferences(false)}>保存匹配偏好</button>
          {active && <button className="button secondary danger-text" disabled={busySection === "preferences"}
            onClick={() => void savePreferences(true)}>保存并暂停</button>}
        </div>}
      </section>

      <section hidden={screen!=="acknowledgements"} className="participation-section" id="acknowledgements" aria-label="加入或恢复匹配前的确认">
        {(["adult_declaration", "matching_pool_acknowledgement"] as const).map((type) => {
          const contract = PARTICIPATION_ACKNOWLEDGEMENTS[type];
          const copy = contract[acknowledgementLocale];
          const accepted = type === "adult_declaration"
            ? participation.acknowledgementVersions.current.adultDeclaration
            : participation.acknowledgementVersions.current.matchingPoolAcknowledgement;
          return <article className="acknowledgement-copy" key={type}>
            <h2>{copy.title}</h2>
            {copy.body.split("\n\n").map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <label className="confirmation-row">
              <input type="checkbox" checked={accepted} disabled={accepted || ended || busySection !== null}
                onChange={(event) => { if (event.target.checked) void acknowledge(type); }} />
              <span>{copy.confirmation}</span>
            </label>
            <p className="ack-status">{accepted ? "已确认当前版本" : "尚未确认"}</p>
          </article>;
        })}
      </section>

      <section hidden={screen!=="preview"} className="participation-section preview-section" id="exact-preview" aria-labelledby="preview-heading">
        <h2 id="preview-heading">对方会看到什么</h2>
        {!ended && <fieldset className="preview-source">
          <legend className="visually-hidden">选择要预览的资料</legend>
          {draftRecord && <label><input type="radio" name="preview-source"
            checked={previewSource === "draft"} onChange={() => setPreviewSource("draft")} /> 私人草稿</label>}
          {profileRead.published && <label><input type="radio" name="preview-source"
            checked={previewSource === "published"} onChange={() => setPreviewSource("published")} /> 当前已发布资料</label>}
        </fieldset>}
        {!ended && <button className="button primary" disabled={busySection === "preview"
          || (previewSource === "draft" ? !draftRecord : !profileRead.published)}
          onClick={() => void createPreview()}>{busySection === "preview" ? "Generating preview" : "预览资料"}</button>}
        {participation.revisions.preview && !participation.revisions.preview.current && <p className="stale-notice">资料、偏好或地点有了变化，请重新预览。</p>}
        {preview && <article className="candidate-preview" aria-label="对方可见的资料预览">
          <h3>{preview.projection.displayName}</h3>
          <p className="preview-meta">{AGE_LABELS[preview.projection.ageBand]}</p>
          {preview.projection.customGenderDisplayLabel && <p>{preview.projection.customGenderDisplayLabel}</p>}
          {preview.projection.pronouns && <p>代词：{preview.projection.pronouns}</p>}
          <p className="preview-prose">{preview.projection.shortIntroduction}</p>
          {preview.projection.beforeYouMeetMeText && <div className="before-meet">
            <p>{preview.projection.beforeYouMeetMeText}</p>
          </div>}
        </article>}
        {!ended&&!active&&<SectionRows><SectionRow disabled={!hasSubmission || !canActOnPreview || profileDirty || preferencesDirty} onClick={()=>go("acknowledgements")}>继续确认参与</SectionRow></SectionRows>}
        {!hasSubmission&&<p>请先填写并提交问卷，草稿仍只对自己可见。</p>}
      </section>

      {screen !== "participation" && participationDecision}

      <section hidden={screen!=="participation" || (!paused && !ended)} className="participation-section lifecycle-zone" aria-labelledby="lifecycle-heading">
        <h2 id="lifecycle-heading">{ended ? "管理保留的资料" : "结束匹配或删除账户"}</h2>
        {paused && <button className="button destructive" onClick={() => endDialog.current?.showModal()}>永久结束匹配</button>}
        {ended && profileRead.published && <button className="button secondary danger-text"
          onClick={() => deleteProfileDialog.current?.showModal()}>删除保留的展示资料</button>}
        <button className="text-button account-separation" onClick={onAccount}>Account & Privacy · 删除账户</button>
      </section>
    </main>
    <ActionDialog dialogRef={leaveDialog} title="尚有未保存的修改" busy={false} onClose={()=>{leaveDialog.current?.close();setLeave(null);}}><p>离开后，这次还没保存的修改会被放弃；已经保存的内容不变。</p><div className="dialog-actions"><button className="button" onClick={()=>{leaveDialog.current?.close();setLeave(null);}}>留在这里</button><button className="button" onClick={()=>{const next=leave;leaveDialog.current?.close();setLeave(null);next?.action();}}>放弃修改并离开</button></div></ActionDialog>
    <ActionDialog dialogRef={logoutDialog} title="退出登录？" busy={false} onClose={()=>logoutDialog.current?.close()}><p>已保存的内容会保留，未保存的资料或偏好会丢失。再次登录需要账户编号与密码。</p><div className="dialog-actions"><button className="button" onClick={()=>logoutDialog.current?.close()}>Cancel</button><button className="button" onClick={onLogout}>Log out</button></div></ActionDialog>
    <ActionDialog dialogRef={pauseDialog} title="暂停匹配"
      busy={busySection === "pause"} onClose={() => pauseDialog.current?.close()}>
      <p className="dialog-copy">暂停后，不会再出现新的人选，也不会收到新的认识邀请。已解锁的联系方式、展示资料、匹配偏好和账户都会保留。恢复前需要重新预览。</p>
      <div className="dialog-actions">
        <button className="button secondary" disabled={busySection === "pause"} onClick={() => pauseDialog.current?.close()}>取消</button>
        <button className="button primary" disabled={busySection === "pause"} onClick={() => void pause()}>确认暂停</button>
      </div>
    </ActionDialog>

    <ActionDialog dialogRef={endDialog} title="永久结束匹配"
      busy={busySection === "end"} onClose={() => { endDialog.current?.close(); setEndPassword(""); }}>
      <p className="dialog-copy">结束后无法恢复匹配，已发布的资料会立即隐藏。账户和已锁定的问卷仍会保留，匹配偏好无法再修改，已解锁的联系也不会自动移除。之后仍可单独删除保留的资料，或删除整个账户。</p>
      <label className="dialog-field"><span>输入确认文字</span>
        <input value={endConfirmation} onChange={(event) => setEndConfirmation(event.target.value)}
          placeholder="PERMANENTLY_END_MATCHING_PARTICIPATION" /></label>
      <label className="dialog-field"><span>当前密码</span>
        <input type="password" autoComplete="current-password" value={endPassword}
          onChange={(event) => setEndPassword(event.target.value)} /></label>
      <div className="dialog-actions">
        <button className="button secondary" disabled={busySection === "end"} onClick={() => { endDialog.current?.close(); setEndPassword(""); }}>取消</button>
        <button className="button destructive" disabled={endConfirmation !== "PERMANENTLY_END_MATCHING_PARTICIPATION" || !endPassword || busySection === "end"}
          onClick={() => void endParticipation()}>永久结束参与</button>
      </div>
    </ActionDialog>

    <ActionDialog dialogRef={deleteProfileDialog} title="删除保留的展示资料"
      busy={busySection === "delete-profile"} onClose={() => deleteProfileDialog.current?.close()}>
      <p className="dialog-copy">账户、已锁定的问卷、匹配偏好、确认记录和已解锁的联系仍会保留。</p>
      <label className="dialog-field"><span>输入确认文字</span>
        <input value={deleteProfileConfirmation}
          onChange={(event) => setDeleteProfileConfirmation(event.target.value)}
          placeholder="DELETE_CANDIDATE_VISIBLE_PROFILE" /></label>
      <div className="dialog-actions">
        <button className="button secondary" onClick={() => deleteProfileDialog.current?.close()}>取消</button>
        <button className="button destructive"
          disabled={deleteProfileConfirmation !== "DELETE_CANDIDATE_VISIBLE_PROFILE" || busySection === "delete-profile"}
          onClick={() => void deleteRetainedProfile()}>删除保留资料</button>
      </div>
    </ActionDialog>
  </ParticipantFrame>;

  const closeThread = () => { setThread(null); setAboutOpen(false); void conversations.refresh(); };
  const overlay = thread && (aboutOpen
    ? <ConversationAbout summary={thread} onBack={() => setAboutOpen(false)} onChanged={setThread} onRemoved={closeThread} />
    : <ConversationThread key={thread.connectionId} initial={thread} onBack={closeThread} onAbout={() => setAboutOpen(true)}
      onChanged={() => void conversations.refresh()} />);
  return <>
    <div className="r4-layer" inert={overlay ? true : undefined}>{mainFrame}</div>
    {overlay}
  </>;
}
